import { uniq, uniqWith } from 'es-toolkit';
import React, { useState } from 'react';
import { shallowEqual } from 'react-redux';
import SwapIcon from '@mui/icons-material/SwapHoriz';
import { Box, Button } from '@mui/material';

import { Lumber, LumberFilter } from '@paragon/contract-library';
import { PropertiesSubpanel } from '@paragon/layout-models';
import {
  getLoggedInUsersOrganizationMaterialSet,
  hasNominalDimensions,
  lumbersAreEqual,
} from '@paragon/material-lists';
import { enqueueError, enqueueSuccess, useConfirmationMessageGenerator } from '@paragon/notification-tools';
import { notNullish } from '@paragon/utilities';

import { RootState } from '../../../../store';
import { closeReplaceMaterialsPanel } from '../../../../store/actions/appActions';
import { replaceLumbers } from '../../../../store/actions/editorActions';
import { getLumbersPanelComponentDesignTargets } from '../../../../store/selectors/getLumbersPanelComponentDesignTargets';
import { MaterialListLumberPicker } from '../../../controls/MaterialListLumberPicker';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import SettingsPanelBase from '../settings/SettingsPanelBase';
import { LumberWhereFilter } from './LumberWhereFilter';

function mapStateToProps(state: RootState) {
  const { replaceMaterialsPanel } = state.panelData;

  if (replaceMaterialsPanel == null || replaceMaterialsPanel.replace !== 'lumbers') {
    throw new Error('ReplaceLumberPanel is being rendered without being open in redux state.');
  }

  const componentDesignTargets = getLumbersPanelComponentDesignTargets(state, replaceMaterialsPanel.targetGuid);
  const organizationInventory = getLoggedInUsersOrganizationMaterialSet(state);

  const sourceLumberListGuids = componentDesignTargets.map((design) => design.materialSelection?.lumberListGuid);
  const sourceLumberListLumbers = uniq(sourceLumberListGuids)
    .filter(notNullish)
    .flatMap((lumberListGuid) => organizationInventory?.lumberLists[lumberListGuid]?.stockLumbers ?? [])
    .map((stockLumber) => stockLumber.lumber);
  const sourceLumbers = uniqWith(sourceLumberListLumbers, lumbersAreEqual);

  return {
    sourceLumbers,
    componentDesignGuids: componentDesignTargets.map((design) => design.guid),
  };
}

const continueMessage = 'Please ensure this is the desired behavior and click "Replace Lumbers" again to continue.';

export function ReplaceLumbersPanel() {
  const dispatch = useAppDispatch();

  const [filter, setFilter] = useState<LumberFilter>({});
  const [newLumber, setNewLumber] = useState<Lumber | null>(null);

  const [isReplacing, setIsReplacing] = useState(false);

  const stateProps = useAppSelector(mapStateToProps, shallowEqual);

  const { ConfirmationArea, isConfirmed, resetConfirmationAnd } = useConfirmationMessageGenerator('warning', () => {
    if (newLumber) {
      if (!hasNominalDimensions(filter)) {
        let example = '2x6';
        if (newLumber.nominalThickness === '2' && newLumber.nominalWidth === '6') {
          example = '2x4';
        }

        return `Note that you have not filtered members based on their dimension.  This means that, for example, ${example} members that match your other conditions will become ${newLumber.nominalThickness}x${newLumber.nominalWidth}.  Additionally, changes that would cause a component to have a different thickness will be ignored. ${continueMessage}`;
      }

      if (newLumber.nominalThickness !== filter.nominalThickness || newLumber.nominalWidth !== filter.nominalWidth) {
        return `You have selected a new dimension that does not match the filtered dimension.  This will cause matching members to have a different dimension after this operation.  Changes that would cause a component to have a different thickness will be ignored.  ${continueMessage}`;
      }
    }
  });

  async function submit() {
    if (newLumber && isConfirmed()) {
      setIsReplacing(true);

      let successfulComponentNames;
      try {
        successfulComponentNames = await dispatch(
          replaceLumbers(stateProps.componentDesignGuids, { filter, newLumber }),
        );
      } catch (error) {
        console.error(error);
        dispatch(enqueueError('Error while replacing lumbers.  Please try again or contact Paragon Customer Support.'));
        return;
      } finally {
        setIsReplacing(false);
      }

      dispatch(closeReplaceMaterialsPanel());

      let successMessage = 'Finished replacing lumbers!';
      if (successfulComponentNames.length > 0) {
        successMessage = `${successMessage} Affected components: ${successfulComponentNames.join(', ')}.`;
      }

      dispatch(enqueueSuccess(successMessage));
    }
  }

  async function cancel() {
    dispatch(closeReplaceMaterialsPanel());
  }

  return (
    <SettingsPanelBase
      title="Replace Lumber"
      saveButtons={
        <Box sx={{ display: 'flex' }}>
          <Button variant="outlined" onClick={cancel} sx={{ m: 1, ml: 'auto' }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={submit} disabled={isReplacing} sx={{ m: 1 }}>
            <SwapIcon /> Replace Lumber
          </Button>
        </Box>
      }
    >
      <ConfirmationArea sx={{ whiteSpace: 'normal', mb: 1 }} />

      <PropertiesSubpanel title="Where:">
        <LumberWhereFilter
          value={filter}
          onChange={resetConfirmationAnd(setFilter)}
          lumbers={stateProps.sourceLumbers}
        />
      </PropertiesSubpanel>

      <PropertiesSubpanel title="Set To:">
        <MaterialListLumberPicker value={newLumber} onChange={resetConfirmationAnd(setNewLumber)} />
      </PropertiesSubpanel>
    </SettingsPanelBase>
  );
}
